// Small, pure helpers the workspace screens share. No React here.

export const PLAN_LABELS = { trial: "Trial", ultimate: "Ultimate", ultimatePlus: "Ultimate Plus", expired: "Expired" };
export const STATUS_LABELS = { trialing: "Trialing", active: "Active", past_due: "Payment overdue", canceled: "Canceled", expired: "Expired" };
export const STATUSES = ["trialing", "active", "past_due", "canceled", "expired"];

export const fullName = (person = {}) => [person.firstName, person.lastName].filter(Boolean).join(" ") || person.email || "";

export const initials = (person = {}) =>
  [person.firstName, person.lastName]
    .filter(Boolean)
    .map((part) => String(part).trim()[0])
    .join("")
    .toUpperCase() || String(person.email || person.name || "?")[0].toUpperCase();

export const nameInitials = (name = "") =>
  String(name)
    .split(/[\s-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase() || "?";

export const planLabel = (plan) => (plan ? PLAN_LABELS[plan] || plan : "No plan");

// "4 of 10 seats used (2 invitations pending)"
export const seatText = (seats) => {
  if (!seats) return "";
  const pending = seats.pending ? ` (${seats.pending} invitation${seats.pending === 1 ? "" : "s"} pending)` : "";
  if (!seats.allowed) return `${seats.used} ${seats.used === 1 ? "person" : "people"}, no seat limit${pending}`;
  return `${seats.used} of ${seats.allowed} seats used${pending}`;
};

export const seatsFull = (seats) => Boolean(seats?.allowed && seats.used >= seats.allowed);

export const formatDate = (value) => {
  if (!value) return "";
  // Dates stored as YYYY-MM-DD read at noon, so no timezone shows the day before.
  const date = /^\d{4}-\d{2}-\d{2}$/.test(String(value)) ? new Date(`${value}T12:00:00`) : new Date(value);
  return isNaN(date.getTime()) ? "" : date.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
};

// The console's workspace list: search by name, admin or database; filters
// "noAdmin" (run by bpExperts) and "several" (people in more than one workspace).
export const filterWorkspaces = (workspaces = [], { query = "", filter = "all" } = {}) => {
  const needle = query.trim().toLowerCase();
  return workspaces.filter((workspace) => {
    if (filter === "noAdmin" && !workspace.noAdmin) return false;
    if (filter === "several" && !workspace.inSeveral) return false;
    if (!needle) return true;
    return [workspace.name, ...(workspace.admins || []).flatMap((a) => [a.name, a.email]), ...(workspace.databases || []).map((d) => d.label)]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(needle));
  });
};

export const filterPeople = (people = [], query = "") => {
  const needle = query.trim().toLowerCase();
  if (!needle) return people;
  return people.filter((person) =>
    [person.email, person.firstName, person.lastName, ...(person.workspaces || []).map((w) => w.name)]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(needle))
  );
};

// The group rule (one view per group): while a workspace has no group,
// everyone in it views every database; once it has one, a member sees a
// database only through a group that has both a database and a view.
export const NO_GROUPS_RULE = "While the workspace has no groups, everyone in it views every database. Once it has one, people see a database only through a group.";
export const OUTSIDE_GROUPS_RULE = "Members only see a database through a group with both a database and a view.";

// What a group holds, in words: "3 people".
export const groupSummary = (group) => {
  const count = group.memberCount ?? (group.members || []).length;
  return `${count} ${count === 1 ? "person" : "people"}`;
};

// A group's one database, from either shape the server sends.
export const groupDatabase = (group) => group?.database || (group?.databases || [])[0] || null;

// A group grants something only with a database and an active view.
export const groupGrants = (group) => Boolean(groupDatabase(group) && group?.view && !group.view.archived);

// The group editor's draft back to what PUT /groups/:id takes:
// { name, members, database, viewId, mode }.
export const groupPayload = (draft) => ({
  name: String(draft.name || "").trim(),
  members: [...new Set(draft.members || [])],
  database: draft.database || null,
  viewId: draft.viewId || null,
  mode: draft.edit ? "edit" : "view",
});

// ── Views ──────────────────────────────────────────────────────────────────
// The view file, exactly: { view, objects: [{ type, label?, attributes }] }.
// "name" always comes first (it is shown anyway).
export const viewFile = (name, objects = []) => ({
  view: String(name || ""),
  objects: (objects || []).map((object) => {
    const attributes = ["name", ...(object.attributes || []).filter((a) => a !== "name")];
    return object.label ? { type: object.type, label: object.label, attributes } : { type: object.type, attributes };
  }),
});

export const viewFileName = (name) => `${String(name || "view").replace(/[\\/:*?"<>|]+/g, "-").trim() || "view"}.view.json`;

export const viewFileText = (name, objects) => `${JSON.stringify(viewFile(name, objects), null, 2)}\n`;

// What a type starts with when it is switched on in the editor.
export const startingAttributes = (available = []) => (available.includes("Description") ? ["name", "Description"] : ["name"]);

// Every detail the objects of a type carry, the name first.
export const allAttributes = (available = []) => ["name", ...(available || []).filter((a) => a !== "name")];

// "Held by Ann Lee and 3 others" / "Nobody holds it yet". A view is given to
// people directly now (28 Sep 2026), so what matters is who has it - each
// one arriving as { userId, email, name, mode }.
export const usedByText = (view) => {
  const people = view?.usedBy || [];
  if (people.length === 0) return "Nobody holds it yet";
  const [first, ...rest] = people.map((person) => person.name || person.email);
  if (rest.length === 0) return `Held by ${first}`;
  if (rest.length === 1) return `Held by ${first} and 1 other`;
  return `Held by ${first} and ${rest.length} others`;
};

// "For Acme library" / why a view grants nothing. A view belongs to exactly
// one database; without one - or with one that was archived or taken off the
// workspace - it shows nobody anything, and says so in the server's words.
export const viewDatabaseText = (view) => {
  if (!view || view.builtIn) return "";
  if (view.databaseNote) return view.databaseNote;
  return view.databaseLabel ? `For ${view.databaseLabel}` : "";
};

// True while a view actually gives its holders a database.
export const viewGrants = (view) => Boolean(view && !view.builtIn && view.grants !== false && view.database);

// Opening a view for editing looks at its own database first (product
// owner, 28 Sep 2026: "when I edit a View it should by default preselect its
// database ... they can still choose another if they want" - that is the
// library the admin is really editing). `viewDatabase` is the view's own
// database name; it wins only while the workspace still offers it - a view
// whose database was archived or taken off the workspace, or one with no
// database yet, falls back to what the picker always opened on: the
// workspace's first database.
export const editorDefaultDatabase = (viewDatabase, databases = []) => {
  if (viewDatabase && (databases || []).some((d) => d.name === viewDatabase)) return viewDatabase;
  return databases?.[0]?.name || "";
};

// ── The built-in view ──────────────────────────────────────────────────────
// Every database has one view nobody has to build: everything in it except
// the diagrams, kept in step with the database on its own. It can't be
// renamed, changed or archived - only copied into a view of your own.
export const BUILTIN_VIEW_ID = "builtin:all";
export const BUILTIN_VIEW_NOTE = "Everything in the database except diagrams — kept up to date automatically.";
export const isBuiltInView = (view) => Boolean(view && (view.builtIn || view.id === BUILTIN_VIEW_ID));

// ── Keeping a view in step with its database ───────────────────────────────
// What a view says, beside what the database actually holds:
//   newTypes     - in the database, not in this view (people can't see them)
//   missingTypes - in the view, no longer in this database
// Diagram types, the explorer's root and types with no objects are never
// "new" (the server's own rule): a view doesn't list them, and a visible
// object's diagrams open anyway.
export const viewStale = (objects = [], types = []) => {
  const listed = new Set((objects || []).map((o) => o.type));
  const present = new Set((types || []).map((t) => t.type));
  return {
    newTypes: (types || []).filter((t) => !t.isDiagram && !t.isRoot && t.objects > 0 && !listed.has(t.type)).map((t) => t.type),
    missingTypes: (objects || []).filter((o) => !present.has(o.type)).map((o) => o.type),
  };
};

// What the chip on a view row says, or "" when there is nothing to say. The
// server answers counts, the screen's own comparison answers lists: both read.
const howMany = (value) => (Array.isArray(value) ? value.length : Number(value) || 0);

export const staleChipText = ({ newTypes = [], missingTypes = [] } = {}) => {
  const added = howMany(newTypes);
  const gone = howMany(missingTypes);
  const parts = [];
  if (added) parts.push(`${added} new object ${added === 1 ? "type" : "types"}`);
  if (gone) parts.push(`${gone} not in this database`);
  return parts.join(" · ");
};

// The banner's sentence: what happened, and what it means for people.
export const staleBannerText = (newTypes = [], databaseLabel = "") => {
  const count = newTypes.length;
  const where = databaseLabel ? ` to ${databaseLabel}` : "";
  return `${count} object ${count === 1 ? "type was" : "types were"} added${where}. People with this view can't see ${count === 1 ? "it" : "them"} yet.`;
};

// ── A library too big to read in one pass ──────────────────────────────────
// The catalogue never fails on a large database - it just doesn't manage to
// read every type's details inside its time budget. `counts` says which:
// "full" needs no comment; "partial" (the rare types were capped) or
// "unknown" (nothing was read this time) get one calm line at the top of the
// editor, so a type that looks empty reads as "not read yet", never as
// broken. "" for "full" (or an older server that doesn't send `counts` at
// all), so the ordinary case gains no new text.
export const catalogueCountsNotice = (counts) => {
  if (counts === "partial") return "This library is large, so details were read for its biggest object types. Open a type to read its details.";
  if (counts === "unknown") return "This library is large, so details couldn't be read this time. Open a type to try, or use Refresh to read them again.";
  return "";
};

// What a "start from a database" dialog (New view, Start from everything, or
// the group editor's inline one) says when that database's catalogue is
// degraded: the new view still lists every type, just with less than usual -
// only its name, sometimes - for the ones this session didn't read.
export const degradedStartNote = (counts) =>
  counts === "partial" || counts === "unknown" ? "Details for some object types weren't read; you can add them later." : "";

// ── What a database's objects actually carry ───────────────────────────────
// A catalogue type's attributes, in either shape the server answers:
// ["Description", …] or [{ name, filled, objects, example }] - the second
// says how many objects of that type hold a value, and shows one.
export const attributeFacts = (entry) =>
  (entry?.attributes || [])
    .map((attribute) => {
      if (typeof attribute === "string") return { name: attribute, filled: null, objects: entry?.objects ?? null, example: "" };
      const value = attribute?.example;
      return {
        name: attribute?.name || attribute?.attribute || "",
        filled: typeof attribute?.filled === "number" ? attribute.filled : null,
        objects: typeof attribute?.objects === "number" ? attribute.objects : entry?.objects ?? null,
        example: value === null || value === undefined ? "" : String(value),
      };
    })
    .filter((fact) => fact.name);

export const attributeNames = (entry) => attributeFacts(entry).map((fact) => fact.name);

// "In 412 of 560 objects", or nothing where the catalogue doesn't count.
export const filledText = ({ filled, objects } = {}) =>
  typeof filled === "number" && typeof objects === "number" && objects > 0 ? `In ${filled} of ${objects} objects` : "";

// "Only details most objects have": held by half of them or more.
export const commonAttributes = (facts = []) =>
  facts.filter((f) => typeof f.filled === "number" && typeof f.objects === "number" && f.objects > 0 && f.filled * 2 >= f.objects).map((f) => f.name);

// One example value, short enough to recognise what the detail is.
export const shortExample = (value, length = 60) => {
  const text = String(value === null || value === undefined ? "" : value).replace(/\s+/g, " ").trim();
  if (!text) return "";
  return text.length > length ? `${text.slice(0, length)}…` : text;
};

// The type list as the admin asked for it - by name or by how many objects
// each has - with the types this database has none of quietly at the end.
export const sortTypes = (types = [], sort = "name", labelOf = (type) => type.label || type.type) => {
  const none = (type) => !(type.objects > 0);
  return [...types].sort((a, b) => {
    if (none(a) !== none(b)) return none(a) ? 1 : -1;
    if (sort === "objects" && (b.objects || 0) !== (a.objects || 0)) return (b.objects || 0) - (a.objects || 0);
    return String(labelOf(a)).localeCompare(String(labelOf(b)));
  });
};

// ── Working on several types at once ───────────────────────────────────────
// The same four choices the row menu and the selection bar offer.
export const TYPE_ACTIONS = [
  { key: "show", label: "Show" },
  { key: "hide", label: "Hide" },
  { key: "basics", label: "Name and description only" },
  { key: "all", label: "All details" },
];

// One action over the named types. `available` answers what a type's objects
// carry ({ type: [attribute] }), so "All details" lists exactly those.
export const applyTypeAction = (objects = [], action, types = [], available = {}) => {
  const wanted = new Set(types);
  if (wanted.size === 0) return objects;
  if (action === "hide") return objects.filter((o) => !wanted.has(o.type));
  const attributesFor = (type) => (action === "all" ? allAttributes(available[type] || []) : startingAttributes(available[type] || []));
  const next = objects.map((object) => {
    if (!wanted.has(object.type)) return object;
    // "Show" leaves a type that is already shown exactly as it is.
    if (action === "show") return object;
    return { ...object, attributes: attributesFor(object.type) };
  });
  const listed = new Set(objects.map((o) => o.type));
  const added = types.filter((type) => !listed.has(type)).map((type) => ({ type, attributes: attributesFor(type) }));
  return [...next, ...added];
};

// "Copy details from…": the source type's details, onto the chosen types.
export const copyAttributesFrom = (objects = [], fromType, toTypes = []) => {
  const source = objects.find((o) => o.type === fromType);
  if (!source) return objects;
  const wanted = new Set(toTypes.filter((type) => type !== fromType));
  if (wanted.size === 0) return objects;
  const attributes = ["name", ...(source.attributes || []).filter((a) => a !== "name")];
  return objects.map((object) => (wanted.has(object.type) ? { ...object, attributes: [...attributes] } : object));
};

// ── What one person sees ───────────────────────────────────────────────────
// GET /api/workspaces/:id/users/:userId/access answers
//   { databases: [{ database, label, mode, view: { id, name, builtIn } | null,
//                   types: <object types they see> | null,
//                   source: [{ id, name, mode }] }],
//     idleViews: [{ id, name, database }] }
// `view: null` is an unfiltered database (an admin holding no view for it);
// `types: null` means the database couldn't be read just now. The older
// shape - a bare list, each row naming the groups that gave it - still reads.
export const accessRows = (content) => {
  const rows = Array.isArray(content) ? content : content?.databases || [];
  return rows.map((row) => {
    const view = row.view || null;
    const groups = row.source || row.groups || (row.group ? [row.group] : []);
    return {
      name: row.database || row.name || "",
      label: row.label || row.database || row.name || "",
      mode: row.mode === "edit" ? "edit" : "view",
      view: view ? { id: view.id, name: view.name, builtIn: isBuiltInView(view) } : null,
      objectTypes: typeof row.types === "number" ? row.types : typeof row.objectTypes === "number" ? row.objectTypes : null,
      groups: groups
        .filter(Boolean)
        .map((group) => ({ id: group.groupId || group.id, name: group.groupName || group.name, viewName: group.viewName || null })),
    };
  });
};

// "Through the view Lean" / how they reach a database without holding one.
// Where someone holds several views on one database they merge into one
// name, so each is named beside it - that is what the merged name is made
// of.
export const accessGrantText = (row) => {
  if (!row.groups.length) return row.view ? "Not through a view they hold" : "As an admin of this workspace, not through a view";
  const named = row.groups.map((g) => (g.viewName ? `${g.name} (${g.viewName})` : g.name));
  if (named.length === 1) return `Through the view ${named[0]}`;
  return `Through the views ${named.slice(0, -1).join(", ")} and ${named[named.length - 1]}`;
};

// "Lean + Standard" on its own says nothing about where the halves came from:
// this is the quiet hint under it. Empty where one group, or none, applies.
export const accessMergeText = (row) => {
  if (!row.view || row.groups.length < 2) return "";
  return `Merged from ${row.groups.length} views they hold, so they see everything any of them shows.`;
};

// Which databases this person can actually open in this workspace, in plain
// words - the sentence that answers "why does a member see one database when
// the workspace has two?".
//
// A member reaches a database only through a view they hold; a workspace
// admin reaches every database of the workspace by role. `databases` is the
// workspace's own, `anyViews` whether it has any view at all (with none, the
// legacy rule still lets every member view every database).
export const opensText = (person, databases = [], anyViews = false) => {
  const all = databases.length;
  if (person?.role === "admin") {
    return all ? `Opens all ${all} ${all === 1 ? "database" : "databases"}, as a workspace admin` : "Opens every database this workspace is given";
  }
  const labelOf = (name) => databases.find((d) => (d.name || d) === name)?.label || name;
  const names = [...new Set((person?.views || []).map((view) => view.database).filter(Boolean))];
  if (names.length) {
    const listed = names.map(labelOf).join(", ");
    return all > names.length ? `Opens ${names.length} of ${all}: ${listed}` : `Opens ${listed}`;
  }
  if (!anyViews) return all ? `Opens all ${all} ${all === 1 ? "database" : "databases"}: no view has been made yet` : "Nothing to open yet";
  return "Opens nothing yet - give them a view";
};

// The views a person holds that give them nothing: no database yet, or one
// this workspace no longer owns.
export const idleViews = (content) => (Array.isArray(content) ? [] : content?.idleViews || []);

// "12 object types" / "Every object type" / "" when the database was away.
export const accessTypesText = (row) => {
  if (row.objectTypes === null || row.objectTypes === undefined) return "";
  return `${row.objectTypes} object ${row.objectTypes === 1 ? "type" : "types"}`;
};
